import type { Job } from '@/domains/jobs/components/JobCard';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { addDays, format } from 'date-fns';
import {
  CheckCircle,
  Gauge,
  Minus,
  Sparkles,
  Target,
  TrendingDown,
  TrendingUp,
  Truck,
  Users,
  Wrench,
  X,
} from 'lucide-react';
import { useState } from 'react';

interface AIAutoScheduleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobs: Job[];
  onUpdateJob: (jobId: string, updates: Partial<Job>) => void;
}

export interface AutoScheduleOptions {
  includeCompetency: boolean;
  includeVehicleAvailability: boolean;
  includePerformance: boolean;
  basicMode: boolean;
}

export function AIAutoScheduleModal({
  open,
  onOpenChange,
  jobs,
  onUpdateJob,
}: AIAutoScheduleModalProps) {
  const [options, setOptions] = useState<AutoScheduleOptions>({
    includeCompetency: true,
    includeVehicleAvailability: true,
    includePerformance: true,
    basicMode: false,
  });

  if (!open) return null;

  const handleToggle = (key: keyof AutoScheduleOptions) => {
    if (key === 'basicMode') {
      // Toggle basic mode on/off
      if (options.basicMode) {
        // Turning off basic mode, restore defaults
        setOptions({
          includeCompetency: true,
          includeVehicleAvailability: true,
          includePerformance: true,
          basicMode: false,
        });
      } else {
        // Turning on basic mode
        setOptions({
          includeCompetency: false,
          includeVehicleAvailability: false,
          includePerformance: false,
          basicMode: true,
        });
      }
    } else {
      // Toggle advanced options
      setOptions({
        ...options,
        [key]: !options[key],
        basicMode: false, // Turn off basic mode when any advanced option is toggled
      });
    }
  };

  const handleAutoSchedule = () => {
    // Get unscheduled jobs
    const unscheduledJobs = jobs.filter((job) => !job.scheduledDate);

    // Teams available for scheduling
    const teams = ['1', '2', '3', '4', '5'];

    // Start scheduling from today
    const today = new Date();

    unscheduledJobs.forEach((job, index) => {
      // Simulate AI scheduling logic based on selected options
      let teamIndex: number;
      let dateOffset: number;

      if (options.basicMode) {
        // Basic mode: Simple round-robin assignment
        teamIndex = index % teams.length;
        dateOffset = Math.floor(index / teams.length);
      } else {
        // Advanced mode: More sophisticated logic
        if (options.includeCompetency) {
          // Match based on job type and team specialization
          if (job.tags.includes('Inspection')) {
            teamIndex = 2; // Quality Control
          } else if (job.tags.includes('Maintenance') || job.tags.includes('Repair')) {
            teamIndex = 1; // Maintenance
          } else if (job.tags.includes('Monitoring') || job.tags.includes('Automation')) {
            teamIndex = 3; // IT & Automation
          } else if (job.tags.includes('Compliance') || job.tags.includes('Permit')) {
            teamIndex = 4; // Compliance
          } else {
            teamIndex = 0; // Operations
          }
        } else {
          teamIndex = index % teams.length;
        }

        if (options.includePerformance) {
          // Distribute high-priority jobs earlier
          if (job.priority === 'high') {
            dateOffset = Math.floor(index / (teams.length * 2));
          } else if (job.priority === 'medium') {
            dateOffset = Math.floor(index / teams.length) + 1;
          } else {
            dateOffset = Math.floor(index / teams.length) + 2;
          }
        } else {
          dateOffset = Math.floor(index / teams.length);
        }

        if (options.includeVehicleAvailability) {
          // Add slight offset to account for vehicle scheduling
          dateOffset += 1;
        }
      }

      // Calculate the scheduled date
      const scheduledDate = format(addDays(today, dateOffset), 'yyyy-MM-dd');

      // Update the job with scheduling information
      onUpdateJob(job.id, {
        scheduledDate,
        teamId: teams[teamIndex],
      });
    });

    onOpenChange(false);
  };

  const insightsData = [
    {
      label: 'Team Availability',
      value: '87%',
      trend: 'up',
      change: '+12%',
      icon: Users,
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-100 dark:bg-green-950/50',
    },
    {
      label: 'Worker Volume',
      value: '142',
      trend: 'stable',
      change: 'Normal',
      icon: Gauge,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-100 dark:bg-blue-950/50',
    },
    {
      label: 'Vehicle Availability',
      value: '78%',
      trend: 'down',
      change: '-5%',
      icon: Truck,
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-100 dark:bg-orange-950/50',
    },
    {
      label: 'Job Completion',
      value: '94%',
      trend: 'up',
      change: '+3%',
      icon: Target,
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-100 dark:bg-purple-950/50',
    },
  ];

  const advancedOptionsActive =
    options.includeCompetency || options.includeVehicleAvailability || options.includePerformance;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
      />

      {/* Modal */}
      <div className="relative bg-background border-2 border-border rounded-xl shadow-2xl max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 p-5 border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center shadow-lg">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground">AI-Enabled Auto-Schedule</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Optimize job assignments using intelligent algorithms
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="bg-background/80 border-border hover:bg-accent"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-5">
            <div className="grid grid-cols-2 gap-5">
              {/* Left Column - Insights */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground text-sm">Current Insights</h3>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {insightsData.map((insight) => (
                    <div
                      key={insight.label}
                      className="p-3 bg-gradient-to-br from-card to-card/50 border border-border rounded-lg"
                    >
                      <div className="flex items-start justify-between mb-1.5">
                        <div
                          className={`w-8 h-8 rounded-lg ${insight.bgColor} flex items-center justify-center`}
                        >
                          <insight.icon className={`w-4 h-4 ${insight.color}`} />
                        </div>
                        <div className="flex items-center gap-0.5">
                          {insight.trend === 'up' && (
                            <TrendingUp className="w-3 h-3 text-green-600 dark:text-green-400" />
                          )}
                          {insight.trend === 'down' && (
                            <TrendingDown className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                          )}
                          {insight.trend === 'stable' && (
                            <Minus className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-foreground">{insight.value}</div>
                        <div className="text-xs text-muted-foreground font-medium">
                          {insight.label}
                        </div>
                        <Badge
                          variant="outline"
                          className={`text-xs mt-1 ${
                            insight.trend === 'up'
                              ? 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800'
                              : insight.trend === 'down'
                                ? 'bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800'
                                : 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                          }`}
                        >
                          {insight.change}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>

                {/* AI Summary */}
                <div className="p-3 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-foreground text-xs mb-1">
                        AI Recommendation
                      </h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {options.basicMode
                          ? 'Basic mode will assign jobs using simple round-robin distribution. Fast but may not optimise for availability.'
                          : advancedOptionsActive
                            ? `Advanced scheduling with ${[
                                options.includeCompetency && 'competency',
                                options.includeVehicleAvailability && 'vehicles',
                                options.includePerformance && 'performance',
                              ]
                                .filter(Boolean)
                                .join(', ')} will maximise efficiency.`
                            : 'Select at least one option to enable intelligent scheduling.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Scheduling Options */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-purple-100 dark:bg-purple-950/50 flex items-center justify-center">
                    <Wrench className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="font-semibold text-foreground text-sm">Scheduling Parameters</h3>
                </div>

                <div className="space-y-2.5">
                  {/* Basic Mode */}
                  <button
                    onClick={() => handleToggle('basicMode')}
                    className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                      options.basicMode
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
                        : 'border-border bg-card hover:border-blue-300 dark:hover:border-blue-700'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-2.5">
                        <div
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center mt-0.5 transition-colors flex-shrink-0 ${
                            options.basicMode
                              ? 'border-blue-600 bg-blue-600'
                              : 'border-border bg-background'
                          }`}
                        >
                          {options.basicMode && <CheckCircle className="w-3 h-3 text-white" />}
                        </div>
                        <div>
                          <h4 className="font-semibold text-foreground text-sm mb-0.5">
                            Basic Mode
                          </h4>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            Simple round-robin job distribution
                          </p>
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 text-xs"
                      >
                        Simple
                      </Badge>
                    </div>
                  </button>

                  {/* Advanced Mode Divider */}
                  <div className="flex items-center gap-2 py-1">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Advanced
                    </span>
                    <div className="flex-1 h-px bg-border" />
                  </div>

                  {/* Competency */}
                  <button
                    onClick={() => handleToggle('includeCompetency')}
                    disabled={options.basicMode}
                    className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                      options.basicMode
                        ? 'opacity-50 cursor-not-allowed border-border bg-muted'
                        : options.includeCompetency
                          ? 'border-green-500 bg-green-50 dark:bg-green-950/20'
                          : 'border-border bg-card hover:border-green-300 dark:hover:border-green-700'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-2.5">
                        <div
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center mt-0.5 transition-colors flex-shrink-0 ${
                            options.includeCompetency && !options.basicMode
                              ? 'border-green-600 bg-green-600'
                              : 'border-border bg-background'
                          }`}
                        >
                          {options.includeCompetency && !options.basicMode && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold text-foreground text-sm mb-0.5">
                            Worker Competency
                          </h4>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            Match jobs by skill certifications
                          </p>
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        className="bg-green-100 dark:bg-green-950/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800 text-xs"
                      >
                        Recommended
                      </Badge>
                    </div>
                  </button>

                  {/* Vehicle Availability */}
                  <button
                    onClick={() => handleToggle('includeVehicleAvailability')}
                    disabled={options.basicMode}
                    className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                      options.basicMode
                        ? 'opacity-50 cursor-not-allowed border-border bg-muted'
                        : options.includeVehicleAvailability
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/20'
                          : 'border-border bg-card hover:border-orange-300 dark:hover:border-orange-700'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-2.5">
                        <div
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center mt-0.5 transition-colors flex-shrink-0 ${
                            options.includeVehicleAvailability && !options.basicMode
                              ? 'border-orange-600 bg-orange-600'
                              : 'border-border bg-background'
                          }`}
                        >
                          {options.includeVehicleAvailability && !options.basicMode && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold text-foreground text-sm mb-0.5">
                            Vehicle Availability
                          </h4>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            Consider equipment availability
                          </p>
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        className="bg-orange-100 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800 text-xs"
                      >
                        Important
                      </Badge>
                    </div>
                  </button>

                  {/* Performance */}
                  <button
                    onClick={() => handleToggle('includePerformance')}
                    disabled={options.basicMode}
                    className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                      options.basicMode
                        ? 'opacity-50 cursor-not-allowed border-border bg-muted'
                        : options.includePerformance
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/20'
                          : 'border-border bg-card hover:border-purple-300 dark:hover:border-purple-700'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-2.5">
                        <div
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center mt-0.5 transition-colors flex-shrink-0 ${
                            options.includePerformance && !options.basicMode
                              ? 'border-purple-600 bg-purple-600'
                              : 'border-border bg-background'
                          }`}
                        >
                          {options.includePerformance && !options.basicMode && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold text-foreground text-sm mb-0.5">
                            Historical Performance
                          </h4>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            Optimize by completion rates
                          </p>
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        className="bg-purple-100 dark:bg-purple-950/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800 text-xs"
                      >
                        Optimal
                      </Badge>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-muted/30 flex-shrink-0">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              This will schedule all unscheduled jobs based on your selections
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                className="bg-background border-border hover:bg-accent"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleAutoSchedule}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Auto-Schedule Jobs
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
